import { ActivatedRouteSnapshot } from '@angular/router'

/**
 * Paramètres posés par Stripe sur `return_url` au retour d'un `confirmSetup`
 * qui a exigé une redirection (3-D Secure). Leur présence identifie l'entrée,
 * quelle que soit la page qui la reçoit.
 */
const STRIPE_SETUP_PARAMS = [
  'setup_intent',
  'setup_intent_client_secret',
  'redirect_status',
] as const

/**
 * Marqueur posé sur les routes qui n'ont structurellement pas besoin des
 * données chargées par l'écran de lancement (programme, zones, forfaits,
 * stations) : `data: { [BYPASS_LAUNCH_DATA]: true }`.
 */
export const BYPASS_LAUNCH_DATA = 'bypassLaunch'

export function isStripeSetupReturn(url: URL): boolean {
  return STRIPE_SETUP_PARAMS.every((param) => url.searchParams.has(param))
}

/**
 * Seule définition du contournement de l'écran de lancement, partagée par le
 * garde de routage et le traitement des liens profonds : les deux entrées
 * doivent répondre la même chose, sinon une page contournée par l'un est
 * renvoyée vers `/launch` par l'autre.
 *
 * Trois critères, volontairement distincts :
 * - la route se déclare indépendante des données de lancement ;
 * - l'URL est un retour Stripe, quelle que soit la page visée — ces pages ont
 *   besoin du launch en usage normal, mais pas sur ce chemin-là, où une
 *   redirection perdrait les paramètres du SetupIntent ;
 * - une confirmation Stripe est en cours. Ce dernier critère existe parce que
 *   le retour n'arrive pas toujours avec son URL intacte : sur natif, un
 *   repositionnement du chemin servi par la WebView (LiveUpdate) ramène à la
 *   racine et efface les paramètres. Le parcours est alors reconnu malgré
 *   tout, et repris depuis ce qui a été mémorisé avant la confirmation.
 */
export function bypassesLaunch(
  route: ActivatedRouteSnapshot | null,
  url: URL,
  hasPendingStripeSetup = false
): boolean {
  return (
    route?.data?.[BYPASS_LAUNCH_DATA] === true ||
    isStripeSetupReturn(url) ||
    hasPendingStripeSetup
  )
}
